import { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type VerificationCodeInputProps = {
  onComplete: (code: string) => void;
  error?: string;
  isDisabled?: boolean;
  isAutoSubmitDisabled?: boolean;
  length?: number;
  value?: string;
};

export const VerificationCodeInput = ({
  onComplete,
  error,
  isDisabled = false,
  isAutoSubmitDisabled = false,
  length = 6,
  value: controlledValue,
}: VerificationCodeInputProps) => {
  const [internalCode, setInternalCode] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const lastSubmittedCode = useRef<string | null>(null);

  const isControlled = controlledValue !== undefined;
  const verificationCode = isControlled
    ? controlledValue
        .split('')
        .concat(Array(length - controlledValue.length).fill(''))
        .slice(0, length)
    : internalCode;

  const setVerificationCode = (newCode: string[]) => {
    if (!isControlled) {
      setInternalCode(newCode);
    }
    const codeString = newCode.join('');
    if (isAutoSubmitDisabled) {
      onComplete(codeString);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, length);

    if (digits.length === 0) return;

    const newCode = digits.split('').concat(Array(length - digits.length).fill(''));
    setVerificationCode(newCode);

    const focusIndex = Math.min(digits.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  useEffect(() => {
    if (isAutoSubmitDisabled) return;

    const code = verificationCode.join('');
    if (code.length === length && code !== lastSubmittedCode.current && !isDisabled) {
      lastSubmittedCode.current = code;
      onComplete(code);
    }
  }, [verificationCode, isDisabled, isAutoSubmitDisabled, onComplete, length]);

  useEffect(() => {
    const code = verificationCode.join('');
    if (code.length < length) {
      lastSubmittedCode.current = null;
    }
  }, [verificationCode, length]);

  return (
    <Stack space={1}>
      <Stack orientation="horizontal" space={2}>
        {verificationCode.map((digit, index) => (
          <CodeInput
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            $hasError={!!error}
            disabled={isDisabled}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        ))}
      </Stack>
      {error && <Text text={error} font="regular12" color="error" />}
    </Stack>
  );
};

const CodeInput = styled.input<{ $hasError?: boolean }>((p) => ({
  width: pxToRem(40),
  height: pxToRem(40),
  textAlign: 'center',
  fontSize: theme.font.regular14,
  border: `1px solid ${p.$hasError ? theme.color.red100 : theme.color.border10}`,
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.uiTrans5,
  color: theme.color.textPrimary,

  '&:focus': {
    outline: 'none',
    borderColor: theme.color.elementBlue100,
    backgroundColor: theme.color.backgroundLightDark,
  },

  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));
